import React, { useCallback } from 'react';

const H = 10;        // visual handle size px
const TOUCH_H = 28;  // touch/click hit-area px (finger-friendly)
const ROTATE_DIST = 32;

/**
 * Corner resize handles + rotation handle for a selected canvas element.
 * Uses Pointer Events (works for both mouse and touch) with setPointerCapture
 * so drag tracking is smooth even when the pointer leaves the handle area.
 *
 * Props:
 *   x, y           – element position in canvas pixels (needed for anchored resize)
 *   width, height  – element dimensions in canvas pixels
 *   zoom           – current canvas zoom (to convert screen → canvas pixels)
 *   rotation       – current rotation in degrees
 *   uniformScale   – for round tables: keep w = h
 *   onResize(x,y,w,h)    – absolute new bounds during drag
 *   onResizeEnd(x,y,w,h) – absolute new bounds on pointer-up
 *   onRotate(deg)  – if provided, shows rotation handle
 */
export default function ResizeHandle({
  x, y, width, height,
  zoom = 1,
  rotation = 0,
  uniformScale = false,
  onResize,
  onResizeEnd,
  onRotate,
}) {
  const startResize = useCallback((corner, e) => {
    e.stopPropagation();
    e.preventDefault();

    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);

    const startClientX = e.clientX;
    const startClientY = e.clientY;

    // Bounds captured at drag start
    const origX = x;
    const origY = y;
    const origW = width;
    const origH = height;

    let lastX = origX, lastY = origY, lastW = origW, lastH = origH;

    const onMove = (me) => {
      const dx = (me.clientX - startClientX) / zoom;
      const dy = (me.clientY - startClientY) / zoom;

      let nx = origX, ny = origY, nw = origW, nh = origH;

      if (corner === 'se') {
        nw = origW + dx;
        nh = origH + dy;
      } else if (corner === 'sw') {
        nx = origX + dx;
        nw = origW - dx;
        nh = origH + dy;
      } else if (corner === 'ne') {
        ny = origY + dy;
        nw = origW + dx;
        nh = origH - dy;
      } else if (corner === 'nw') {
        nx = origX + dx;
        ny = origY + dy;
        nw = origW - dx;
        nh = origH - dy;
      }

      // Enforce minimum sizes (canvas pixels)
      const minW = 60;
      const minH = uniformScale ? 60 : 40;

      if (nw < minW) {
        if (corner === 'sw' || corner === 'nw') nx = origX + origW - minW;
        nw = minW;
      }
      if (nh < minH) {
        if (corner === 'ne' || corner === 'nw') ny = origY + origH - minH;
        nh = minH;
      }

      if (uniformScale) {
        const size = Math.max(minW, (nw + nh) / 2);
        if (corner === 'nw') { nx = origX + origW - size; ny = origY + origH - size; }
        if (corner === 'ne') { ny = origY + origH - size; }
        if (corner === 'sw') { nx = origX + origW - size; }
        nw = size; nh = size;
      }

      lastX = nx; lastY = ny; lastW = nw; lastH = nh;
      onResize?.(nx, ny, nw, nh);
    };

    const onUp = () => {
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      onResizeEnd?.(lastX, lastY, lastW, lastH);
    };

    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
  }, [x, y, width, height, zoom, uniformScale, onResize, onResizeEnd]);

  const startRotate = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();

    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);

    const parentEl = target.parentElement;
    const rect = parentEl.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    const baseRotation = rotation;

    const onMove = (me) => {
      const currentAngle = Math.atan2(me.clientY - centerY, me.clientX - centerX) * (180 / Math.PI);
      const delta = currentAngle - startAngle;
      const newRotation = Math.round((baseRotation + delta) / 15) * 15;
      onRotate?.(newRotation);
    };
    const onUp = () => {
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
  }, [rotation, onRotate]);

  const corners = [
    { id: 'nw', pos: { top: -TOUCH_H / 2, left: -TOUCH_H / 2 }, cursor: 'nw-resize' },
    { id: 'ne', pos: { top: -TOUCH_H / 2, right: -TOUCH_H / 2 }, cursor: 'ne-resize' },
    { id: 'sw', pos: { bottom: -TOUCH_H / 2, left: -TOUCH_H / 2 }, cursor: 'sw-resize' },
    { id: 'se', pos: { bottom: -TOUCH_H / 2, right: -TOUCH_H / 2 }, cursor: 'se-resize' },
  ];

  return (
    <>
      {/* Rotation handle */}
      {onRotate && (
        <>
          <div style={{
            position: 'absolute',
            left: '50%',
            top: -ROTATE_DIST,
            width: 1,
            height: ROTATE_DIST,
            background: '#2a5d32',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 18,
          }} />
          {/* Large touch area wrapping the small visual circle */}
          <div
            onPointerDown={startRotate}
            title="Forgatás (15°-os lépések)"
            style={{
              position: 'absolute',
              left: '50%',
              top: -ROTATE_DIST - TOUCH_H / 2,
              transform: 'translateX(-50%)',
              width: TOUCH_H,
              height: TOUCH_H,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 22,
              cursor: 'grab',
            }}
          >
            <div style={{
              width: 16,
              height: 16,
              background: '#fff',
              border: '2px solid #2a5d32',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              color: '#2a5d32',
              userSelect: 'none',
              pointerEvents: 'none',
            }}>
              ↻
            </div>
          </div>
        </>
      )}

      {/* Corner resize handles – large touch area with small visual square inside */}
      {corners.map(({ id, pos, cursor }) => (
        <div
          key={id}
          onPointerDown={(e) => startResize(id, e)}
          style={{
            position: 'absolute',
            width: TOUCH_H,
            height: TOUCH_H,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20,
            cursor,
            ...pos,
          }}
        >
          <div style={{
            width: H,
            height: H,
            background: '#fff',
            border: '2px solid #2a5d32',
            borderRadius: 2,
            pointerEvents: 'none',
          }} />
        </div>
      ))}
    </>
  );
}
